import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { MARKUP_KEY_BY_MATRIX, MATRIX_LABEL_BY_KEY, SOURCE_TYPE_BY_MATRIX } from '@crm/shared'
import type { ItemRow } from './itemColDefs'
import type { SourceType } from '../../../shared/api/types'

// Матрица (внутренний ключ колонки) → SourceType квоты и русская метка.
// Словари общие с computeBestPrice/kpPrefillFromSelection (@crm/shared).
const MATRIX_SRC = SOURCE_TYPE_BY_MATRIX as Record<string, SourceType>
const MATRIX_LBL = MATRIX_LABEL_BY_KEY

/** Подсказка в ячейке, когда посчитать рубли нечем. */
const NO_PRICING_TITLE = 'Курс CNY или коэффициенты наценки не загружены'

/** Прямоугольник-якорь ячейки для позиционирования поповера квот. */
export interface CellAnchor {
  top: number
  left: number
  bottom: number
  right: number
}

interface PriceColDefsParams {
  isAdmin: boolean
  userRole: string | undefined
  cnyRate?: number | null
  markupConstants?: Record<string, number> | null
  /** Курсы и коэффициенты загружены. Пока false — рубли не считаем вовсе:
      в ячейках прочерк, «Сумма» пустая. Фолбэк-курса больше нет. */
  pricingReady?: boolean
  kpMode?: boolean
  selectedPrices?: Map<
    string,
    { col: string; matrix: string; cost: number; supplierName: string | null }
  >
  // Клик по ячейке в режиме КП. cellValue — цена в ячейке в её валюте (¥, у РФ
  // ₽): по ней подбирается поставщик строки КП (№73, useKpCellSelect).
  onSelectPrice?: (
    itemId: string,
    col: string,
    matrix: string,
    cost: number,
    cellValue: number,
  ) => void
  // Клик по бейджу-счётчику квот в ячейке матрицы — открыть поповер-список
  // квот этой матрицы (создание/правка/удаление). Только вне режима КП.
  onOpenQuotes?: (
    itemId: string,
    sourceType: SourceType,
    matrixLabel: string,
    anchor: CellAnchor,
  ) => void
}

export function buildPriceColDefs({
  isAdmin,
  userRole,
  cnyRate,
  markupConstants,
  pricingReady,
  kpMode,
  selectedPrices,
  onSelectPrice,
  onOpenQuotes,
}: PriceColDefsParams): ColDef<ItemRow>[] {
  // Себестоимость в ₽. Ни курса, ни коэффициента по умолчанию здесь нет:
  // если справочники не загружены — возвращаем null, и ячейка показывает «—».
  // Раньше вместо этого подставлялись 1 и 1.4, и КП уходил клиенту заниженным.
  const toRub = (raw: number | null | undefined, matrix: string): number | null => {
    if (raw == null) return null
    if (!pricingReady || cnyRate == null || !markupConstants) return null
    const markupKey = MARKUP_KEY_BY_MATRIX[matrix]
    const k = markupKey ? markupConstants[markupKey] : undefined
    if (k == null) return null
    if (matrix === 'rf') return raw * k
    return raw * k * cnyRate
  }

  const getAllCosts = (d: ItemRow) => {
    const costs: { col: string; val: number }[] = []
    // Курсы/коэффициенты не загружены — считать нечего (ни минимума, ни суммы).
    if (!pricingReady) return costs
    const ps = toRub(d.priceStock as number | null, 'stock')
    const pm = toRub(d.priceManufacturer as number | null, 'manuf')
    const po = toRub(d.priceOther as number | null, 'other')
    const pr = toRub(d.priceRF as number | null, 'rf')
    const pc = toRub(d.targetPrice as number | null, 'china')
    // Цена 0 не учитывается: она не может быть «лучшей». Ячейка с нулём
    // не подсвечивается зелёным как минимальная и не попадает в «Сумма».
    if (ps != null && ps > 0) costs.push({ col: 'priceStock', val: ps })
    if (pm != null && pm > 0) costs.push({ col: 'priceManufacturer', val: pm })
    if (po != null && po > 0) costs.push({ col: 'priceOther', val: po })
    if (pr != null && pr > 0) costs.push({ col: 'priceRF', val: pr })
    if (pc != null && pc > 0) costs.push({ col: 'price', val: pc })
    return costs
  }

  const fmtRub = (v: number) => v.toLocaleString('ru-RU', { maximumFractionDigits: 2 }) + ' ₽'

  const priceSetter = (field: string) => (p: { data: ItemRow; newValue: unknown }) => {
    const raw = String(p.newValue ?? '')
      .replace(/\s/g, '')
      .replace(',', '.')
    const num = parseFloat(raw)
    ;(p.data as Record<string, unknown>)[field] = isNaN(num) || num < 0 ? null : num
    return true
  }

  const makePriceCol = (
    colId: string,
    field: string,
    headerName: string,
    matrix: string,
  ): ColDef<ItemRow> => ({
    colId,
    field,
    headerName,
    editable:
      !kpMode &&
      (isAdmin || userRole === 'MANAGER' || userRole === 'ROP' || userRole === 'ASSISTANT'),
    // В режиме КП всю ячейку занимает интерактивный блок — убираем
    // стандартный padding AG Grid, чтобы подсветка/рамка охватывали ячейку целиком.
    cellClass: kpMode ? 'ag-cell-no-padding' : undefined,
    cellRenderer: (p: ICellRendererParams<ItemRow>) => {
      if (!p.data) return null
      const itemId = p.data.id as string
      const srcType = MATRIX_SRC[matrix]
      const results = (p.data.results as { sourceType: string }[] | undefined) ?? []
      const quoteCount = results.filter((r) => r.sourceType === srcType).length

      // Бейдж-счётчик квот матрицы. Клик открывает поповер-список квот
      // (создание/правка/удаление). Только вне режима КП. onMouseDown гасим,
      // чтобы клик по бейджу не запускал диапазонное выделение ячеек.
      const badge =
        !kpMode && quoteCount > 0 && onOpenQuotes ? (
          <button
            type="button"
            onMouseDown={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              const cell = (e.currentTarget.closest('.ag-cell') ?? e.currentTarget) as HTMLElement
              const r = cell.getBoundingClientRect()
              onOpenQuotes(itemId, srcType, MATRIX_LBL[matrix] ?? matrix, {
                top: r.top,
                left: r.left,
                bottom: r.bottom,
                right: r.right,
              })
            }}
            title={`${quoteCount} квот(ы) по матрице — открыть список`}
            className="absolute top-0 left-0 min-w-[15px] h-[15px] px-[3px] flex items-center justify-center rounded-br bg-brand-100 text-brand-700 text-[9px] font-semibold leading-none hover:bg-brand-200 z-10"
          >
            {quoteCount}
          </button>
        ) : null

      const raw = p.data[field] as number | null
      if (raw == null) {
        if (kpMode) return null
        return (
          <div className="relative h-full w-full flex items-center justify-end py-0.5">
            <span className="text-xs text-gray-300">—</span>
            {badge}
          </div>
        )
      }
      const cost = toRub(raw, matrix)
      if (cost == null) {
        // Цена в позиции есть, но перевести её в рубли нечем. Показываем
        // прочерк и сырое значение — заниженную цифру рисовать нельзя.
        return (
          <div
            title={NO_PRICING_TITLE}
            className="relative h-full w-full flex flex-col items-end justify-center py-0.5 text-xs leading-tight"
          >
            <span className="text-gray-400">—</span>
            <span className="text-gray-400 text-[10px]">
              {raw} {matrix === 'rf' ? '₽' : '¥'}
            </span>
            {badge}
          </div>
        )
      }
      const costs = getAllCosts(p.data)
      const minCost = costs.length > 0 ? Math.min(...costs.map((c) => c.val)) : null
      const isBest = minCost != null && costs.length > 1 && Math.abs(cost - minCost) < 0.01
      const isSelected = selectedPrices?.get(itemId)?.col === colId
      const handleClick = kpMode
        ? () => onSelectPrice?.(itemId, colId, matrix, cost, raw)
        : undefined
      return (
        <div
          onClick={handleClick}
          className={[
            'relative text-xs text-right leading-tight',
            kpMode
              ? 'h-full w-full flex flex-col items-end justify-center px-2 cursor-pointer transition-colors'
              : 'py-0.5',
            isSelected ? 'bg-green-100 ring-2 ring-inset ring-green-500' : '',
            kpMode && !isSelected ? 'hover:bg-blue-50' : '',
          ].join(' ')}
        >
          <div
            className={
              isSelected
                ? 'text-green-700 font-bold'
                : isBest
                  ? 'text-green-600 font-bold'
                  : 'font-medium'
            }
          >
            {fmtRub(cost)}
          </div>
          <div className="text-gray-400 text-[10px]">
            {raw} {matrix === 'rf' ? '₽' : '¥'}
          </div>
          {badge}
        </div>
      )
    },
    valueSetter: priceSetter(field),
  })

  return [
    makePriceCol('priceOther', 'priceOther', 'Стоки', 'other'),
    makePriceCol('priceStock', 'priceStock', 'Склад', 'stock'),
    makePriceCol('priceManufacturer', 'priceManufacturer', 'Произв.', 'manuf'),
    makePriceCol('priceRF', 'priceRF', 'РФ', 'rf'),
    makePriceCol('price', 'targetPrice', 'Китай', 'china'),
    {
      colId: 'total',
      headerName: 'Сумма (₽)',
      sortable: false,
      cellClass: 'text-xs text-right font-medium',
      valueGetter: (p: { data: ItemRow | undefined }) => {
        if (!p.data) return null
        const qty = p.data.quantity as number | undefined
        if (!qty) return null
        const costs = getAllCosts(p.data)
        if (costs.length === 0) return null
        const best = Math.min(...costs.map((c) => c.val))
        return qty * best
      },
      valueFormatter: (p: { value: unknown }) =>
        p.value != null
          ? (p.value as number).toLocaleString('ru-RU', { maximumFractionDigits: 2 }) + ' ₽'
          : '—',
    },
  ]
}
